import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import type { ChartSeries, ChartSeriesToken } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { token } from '../../../tokens/tokens';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ShareOfPositiveView = {
  /** `Your share of positive`, `Rank`, `Leader`. */
  kpis: KPIMetric[];
  /** One row per brand, highest positive share first; one key, `positiveShare`. */
  rows: ChartRow[];
  /** The brand option value behind each row, for the drill-down. */
  keys: string[];
  xKey: string;
  series: ChartSeries[];
  /** Each bar's brand colour, in bar order — the legend's too. */
  bars: { label: string; color: ChartSeriesToken }[];
  /** The positive share across every mention in scope — the dashed reference line. */
  threshold: number;
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type ShareOfPositiveCardProps = WidgetBlockProps & {
  view?: ShareOfPositiveView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `931:36991` — each brand's favourable share against the category mean.
 *
 * Category-scoped: the `Brands` chip does the scoping, so the user can deselect
 * us and "your share of positive" then reads an em dash. The category mean is
 * the share over **every** mention, not the mean of the bars — mention-weighted,
 * so a brand with forty mentions cannot pull the benchmark as hard as one with
 * four thousand.
 */
export function ShareOfPositiveCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: ShareOfPositiveCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Share of positive vs competitors"
      helpText="What proportion of each brand's own mentions are favourable, against the category mean."
      helpSupportingText="Normalised by construction, so the largest operator has no advantage — the dashed line is the share across every mention in the period, not the average of the five bars."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            threshold={view.threshold}
            /* Each bar in its own brand's colour, so a brand reads the same here
               as it does in the donut above and the trend below. */
            cellColors={view.bars.map((bar) => bar.color)}
            formatValue={view.formatValue}
          />
        )
      }
      /* Keyed by brand, in bar order, so each colour reads the same as the donut's. */
      legend={
        view && <ChartLegend items={view.bars.map((bar) => ({ label: bar.label, color: token[bar.color] }))} />
      }
    />
  );
}
